import {
  ChangeEvent,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Button,
  IconButton,
  InputBase,
  Paper,
  SxProps,
  Theme,
} from '@mui/material'

import CancelIcon from '@mui/icons-material/Cancel'
import SearchIcon from '@mui/icons-material/Search'

import responsiveHook from '@hooks/useResponsive'

import {
  clearSearchButtonSx,
  clearSearchIconSx,
  containerSx,
  contentSx,
  embeddedSearchButtonSx,
  embeddedSearchIconSx,
  inputBaseSx,
  searchButtonSx,
  searchIconButtonSx,
  searchIconSx,
} from './styles'

export enum SearchBarVariant {
  DEFAULT = 'default',
  LANDING_PAGE = 'landing-page',
  CATALOG = 'catalog',
}

export interface ISearchBarProps {
  placeholder: string
  additionalContainerSx?: SxProps<Theme>
  variant?: SearchBarVariant
  initialValue?: string
  maxLength: number
  onValueChange?: (value: string) => void
  onSearch: (value: string) => void
  onClear?: () => void
}

export interface ISearchBarRefProps {
  clearSearch: () => void
  setSearchValue: (value: string) => void
}

const ForwardedSearchBar: ForwardRefRenderFunction<
  ISearchBarRefProps,
  ISearchBarProps
> = (
  {
    placeholder,
    additionalContainerSx,
    variant = SearchBarVariant.DEFAULT,
    initialValue,
    maxLength,
    onValueChange,
    onSearch,
    onClear,
  },
  ref
) => {
  const { t } = useTranslation()
  const { isMobile } = responsiveHook.useResponsive()

  const [search, setSearch] = useState(initialValue ?? '')
  const isDefaultStyle = variant === SearchBarVariant.DEFAULT
  const isLandingPageStyle = variant === SearchBarVariant.LANDING_PAGE
  const isCatalogStyle = variant === SearchBarVariant.CATALOG

  useImperativeHandle(ref, () => {
    return {
      clearSearch() {
        setSearch('')
      },
      setSearchValue(value: string) {
        setSearch(value)
      },
    }
  })

  const handleOnSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    onSearch(search)
  }

  const handleOnClearSearchButtonClick = () => {
    setSearch('')
    onClear?.()
  }

  const handleOnFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setSearch(value)

    onValueChange?.(value)
  }

  return (
    <Box
      sx={{ ...containerSx, ...additionalContainerSx } as SxProps<Theme>}
      component="form"
      onSubmit={handleOnSearch}
    >
      <Paper elevation={0} sx={contentSx({ variant })}>
        {(isDefaultStyle || isCatalogStyle) && <SearchIcon sx={searchIconSx} />}
        <InputBase
          placeholder={placeholder}
          value={search}
          onChange={handleOnFieldChange}
          inputProps={{ 'data-testid': 'search-input', maxLength }}
          sx={inputBaseSx({ variant })}
        />

        {search && (
          <IconButton
            data-testid="search-bar-clear-button"
            aria-label={t('Clear search')}
            sx={clearSearchButtonSx}
            onClick={handleOnClearSearchButtonClick}
          >
            <CancelIcon sx={clearSearchIconSx} />
          </IconButton>
        )}
        {!search && isLandingPageStyle && (
          <IconButton onClick={() => onSearch(search)} sx={searchIconButtonSx}>
            <SearchIcon sx={searchIconSx} />
          </IconButton>
        )}
      </Paper>
      {(isDefaultStyle || isCatalogStyle) &&
        (isMobile ? (
          <Button
            startIcon={<SearchIcon sx={embeddedSearchIconSx} />}
            data-testid="search-button"
            variant="contained"
            type="submit"
            sx={embeddedSearchButtonSx({ variant })}
          />
        ) : (
          <Button
            data-testid="search-button"
            variant="contained"
            type="submit"
            sx={searchButtonSx}
          >
            {t('Search')}
          </Button>
        ))}
    </Box>
  )
}

const SearchBar = forwardRef(ForwardedSearchBar)
export { SearchBar }
